import { isEqual } from 'lodash'
import { getPersonListData, _getPDataById } from '../persons/mockData'
import {
  getCompanyListData,
  getCompanyOverviewData,
} from '../companies/mockData'
import {
  ensureUniquenessAfterAddOrChangeToListData,
  propagateAfterAddsChangesOrDeletes,
} from '../../modules/mockDataCommon'
import { _storeEntityDataListToLocalForage } from '../../modules/mockDataStorageManager'
import { getListDataFunctionFactory } from '../../modules/getListDataFunctionFactory'
import { propagateCompanyContactData } from '~/cynqueModules/dataObjects/CompanyContact'
import type { ICompanyContact } from '~/types/company/models'
import type { TEntityId } from '~/types/models'

const mn = '[api/msw/api/companyContacts/mockData.ts]'

const companyContactListData = undefined as unknown as ICompanyContact[]

export const getCompanyContactListData = getListDataFunctionFactory<
  ICompanyContact[]
>({
  listData: companyContactListData,
  storageKey: 'companycontacts',
  getStartingListData: getStartingCompanyContactListData,
})

export function _getCCTDataById(
  id: TEntityId,
  list: ICompanyContact[]
): ICompanyContact | undefined {
  if (!list) {
    throw new Error(`_getCCTDataById called with undefined/null list`)
  }
  const companyContact = list.find((cct: ICompanyContact) => cct.id === id)
  if (!companyContact) {
    // This is a mock-data-only situation. Can't easily reconcile object graph.
    // may still be a reference to a deleted companyContact in, for example,
    // locationCompanyContacts. This is fixable, but not a good use of time
    // since it only affects mock data.
    console.debug(
      `_getCCTDataById WARNING: Can't find companyContact '${id}'. May have been deleted.`
    )
  }
  return companyContact
}

export async function getCompanyContactDataById(
  id: TEntityId
): Promise<ICompanyContact | undefined> {
  const list = await getCompanyContactListData()
  return _getCCTDataById(id, list)
}

export async function getCompanyContactListDataByCompanyId(id: string) {
  const list = await getCompanyContactListData()
  if (!list) {
    throw new Error(`getCompanyContactList returned undefined/null`)
  }
  return (
    list.filter(
      (companyContact: ICompanyContact) =>
        !!companyContact.company && isEqual(companyContact.company.id, id)
    ) ?? []
  )
}

export function _getCompanyPrimaryCCTDataByCompanyId(
  id: TEntityId,
  list: ICompanyContact[]
): ICompanyContact {
  if (!list) {
    throw new Error(
      `_getCompanyPrimaryCCTDataById called with undefined/null list`
    )
  }
  return list.find(
    (companyContact: ICompanyContact) =>
      !!companyContact.company &&
      isEqual(companyContact.company.id, id) &&
      companyContact.isPrimaryContact
  ) as ICompanyContact
}

export function _getCompanyBillingCCTDataByCompanyId(
  id: TEntityId,
  list: ICompanyContact[]
): ICompanyContact {
  if (!list) {
    throw new Error(
      `_getCompanyBillingCCTDataById called with undefined/null list`
    )
  }
  return list.find(
    (companyContact: ICompanyContact) =>
      !!companyContact.company &&
      isEqual(companyContact.company.id, id) &&
      companyContact.isBillingContact
  ) as ICompanyContact
}

export async function getCompanyPrimaryContactDataByCompanyId(
  id: string
): Promise<ICompanyContact> {
  const list = await getCompanyContactListData()
  return _getCompanyPrimaryCCTDataByCompanyId(id, list)
}

export async function getCompanyBillingContactDataByCompanyId(
  id: string
): Promise<ICompanyContact> {
  const list = await getCompanyContactListData()
  return _getCompanyBillingCCTDataByCompanyId(id, list)
}

export async function processAfterAddOrChangeToCompanyContactListData({
  targetItem,
  updatedEntityData,
  listData,
}: {
  targetItem: ICompanyContact
  updatedEntityData: Partial<ICompanyContact>
  listData: ICompanyContact[]
}) {
  const fn = `${mn} processAfterAddOrChangeToCompanyContactListData |`
  console.debug(`${fn} called`)
  await ensureUniquenessAfterAddOrChangeToListData<ICompanyContact>({
    targetItem,
    updatedEntityData,
    listData,
    propertyNameArray: ['isPrimaryContact', 'isBillingContact'],
    entityKeyBase: 'companycontacts',
    getListDataFn: getCompanyContactListData,
    filterFn: (cc: ICompanyContact) => cc.company.id === targetItem.company.id,
  })
  await propagateAfterAddsChangesOrDeletes<ICompanyContact>({
    sourceItem: targetItem,
    sourceEntityName: 'companyContact',
    targetEntityInSourceItem: 'company',
    getOverviewFn: getCompanyOverviewData,
    propagationFn: propagateCompanyContactData,
    getTargetEntityListDataFn: getCompanyListData,
    entityKeyBase: 'companies',
  })
}

export async function processAfterDeleteOfCompanyContactListData({
  deletedEntityData,
}: {
  deletedEntityData: ICompanyContact
}) {
  const fn = `${mn} processAfterDeleteOfCompanyContactListData |`
  console.debug(`${fn} called...`)
  await propagateAfterAddsChangesOrDeletes<ICompanyContact>({
    sourceItem: deletedEntityData,
    sourceEntityName: 'companyContact',
    targetEntityInSourceItem: 'company',
    getOverviewFn: getCompanyOverviewData,
    propagationFn: propagateCompanyContactData,
    getTargetEntityListDataFn: getCompanyListData,
    entityKeyBase: 'companies',
  })
}

export async function getStartingCompanyContactListData(): Promise<
  ICompanyContact[]
> {
  const pList = await getPersonListData()
  return [
    {
      id: 'XXCCT0001',
      hrid: 'CCT0001',
      person: _getPDataById('XXPE0001', pList),
      phone: '(555) 111-2222',
      cell: '(555) 999-8888',
      email: 'isaac@metropolisbank.com',
      company: {
        id: 'XXC0001',
        label: 'Metropolis Bank',
      },
      role: { id: 'XXCCR0003', label: 'Liaison' },
      isPrimaryContact: true,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXCCT0002',
      hrid: 'CCT0002',
      person: _getPDataById('XXPE0002', pList),
      phone: '(555) 222-3333',
      cell: '(555) 999-8887',
      email: 'galileo@metropolisbank.com',
      company: {
        id: 'XXC0001',
        label: 'Metropolis Bank',
      },
      role: { id: 'XXCCR0001', label: 'Billing' },
      isBillingContact: true,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXCCT0003',
      hrid: 'CCT0003',
      person: _getPDataById('XXPE0003', pList),
      phone: '(555) 333-4444',
      cell: '(555) 999-8886',
      email: 'albert@farreachinghealth.com',
      company: {
        id: 'XXC0002',
        label: 'Far Reaching Health',
      },
      role: { id: 'XXCCR0003', label: 'Liaison' },
      isPrimaryContact: true,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXCCT0004',
      hrid: 'CCT0004',
      person: _getPDataById('XXPE0004', pList),
      phone: '(555) 444-5555',
      cell: '(555) 999-8885',
      email: 'richard@farreachinghealth.com',
      company: {
        id: 'XXC0002',
        label: 'Far Reaching Health',
      },
      role: { id: 'XXCCR0001', label: 'Billing' },
      isBillingContact: true,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXCCT0005',
      hrid: 'CCT0005',
      person: _getPDataById('XXPE0005', pList),
      phone: '(555) 111-2222',
      cell: '(555) 999-8884',
      email: 'samwise@bostoncoach.com',
      company: {
        id: 'XXC0003',
        label: 'Boston Coach',
      },
      role: { id: 'XXCCR0003', label: 'Liaison' },
      isPrimaryContact: true,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },

    {
      id: 'XXCCT0006',
      hrid: 'CCT0006',
      person: _getPDataById('XXPE0006', pList),
      phone: '(555) 222-3333',
      cell: '(555) 999-8883',
      email: 'frodo@bostoncoach.com',
      company: {
        id: 'XXC0003',
        label: 'Boston Coach',
      },
      role: { id: 'XXCCR0001', label: 'Billing' },
      isBillingContact: true,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXCCT0007',
      hrid: 'CCT0007',
      person: _getPDataById('XXPE0007', pList),
      phone: '(555) 333-4444',
      cell: '(555) 999-8882',
      email: 'neils@bostonmedicalcenter.com',
      company: {
        id: 'XXC0004',
        label: 'Boston Medical Center',
      },
      role: { id: 'XXCCR0003', label: 'Liaison' },
      isPrimaryContact: true,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXCCT0008',
      hrid: 'CCT0008',
      person: _getPDataById('XXPE0008', pList),
      phone: '(555) 444-5555',
      cell: '(555) 999-8881',
      email: 'marie@bostonmedicalcenter.com',
      company: {
        id: 'XXC0004',
        label: 'Boston Medical Center',
      },
      role: { id: 'XXCCR0001', label: 'Billing' },
      isBillingContact: true,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXCCT0009',
      hrid: 'CCT0009',
      person: _getPDataById('XXPE0009', pList),
      phone: '(555) 111-2222',
      cell: '(555) 999-8879',
      email: 'stephen@fullrackdata.com',
      company: {
        id: 'XXC0005',
        label: 'Full Rack Data Center',
      },
      role: { id: 'XXCCR0003', label: 'Liaison' },
      isPrimaryContact: true,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXCCT0010',
      hrid: 'CCT0010',
      person: _getPDataById('XXPE0010', pList),
      phone: '(555) 222-3333',
      cell: '(555) 999-8878',
      email: 'ernest@fullrackdata.com',
      company: {
        id: 'XXC0005',
        label: 'Full Rack Data Center',
      },
      role: { id: 'XXCCR0001', label: 'Billing' },
      isBillingContact: true,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },

    {
      id: 'XXCCT0011',
      hrid: 'CCT0011',
      person: _getPDataById('XXPE0011', pList),
      phone: '(555) 333-4444',
      cell: '(555) 999-8877',
      email: 'harry@watchco.com',
      company: {
        id: 'XXC0006',
        label: 'Watch Company',
      },
      role: { id: 'XXCCR0003', label: 'Liaison' },
      isPrimaryContact: true,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXCCT0012',
      hrid: 'CCT0012',
      person: _getPDataById('XXPE0012', pList),
      phone: '(555) 444-5555',
      cell: '(555) 999-8876',
      email: 'hermione@watchco.com',
      company: {
        id: 'XXC0006',
        label: 'Watch Company',
      },
      role: { id: 'XXCCR0001', label: 'Billing' },
      isBillingContact: true,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXCCT0013',
      hrid: 'CCT0013',
      person: _getPDataById('XXPE0013', pList),
      phone: '(555) 111-2222',
      cell: '(555) 999-8875',
      email: 'michael@strathamcoach.com',
      company: {
        id: 'XXC0007',
        label: 'Stratham Coach',
      },
      role: { id: 'XXCCR0003', label: 'Liaison' },
      isPrimaryContact: true,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXCCT0014',
      hrid: 'CCT0014',
      person: _getPDataById('XXPE0014', pList),
      phone: '(555) 222-3333',
      cell: '(555) 999-8874',
      email: 'paul@strathamcoach.com',
      company: {
        id: 'XXC0007',
        label: 'Stratham Coach',
      },
      role: { id: 'XXCCR0001', label: 'Billing' },
      isBillingContact: true,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXCCT0015',
      hrid: 'CCT0015',
      person: _getPDataById('XXPE0015', pList),
      phone: '(555) 333-4444',
      cell: '(555) 999-88873',
      email: 'edwin@durhammedical.com',
      company: {
        id: 'XXC0008',
        label: 'Durham Medical Center',
      },
      role: { id: 'XXCCR0003', label: 'Liaison' },
      isPrimaryContact: true,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXCCT0016',
      hrid: 'CCT0016',
      person: _getPDataById('XXPE0016', pList),
      phone: '(555) 444-5555',
      cell: '(555) 999-8872',
      email: 'max@durhammedical.com',
      company: {
        id: 'XXC0008',
        label: 'Durham Medical Center',
      },
      role: { id: 'XXCCR0001', label: 'Billing' },
      isBillingContact: true,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXCCT0017',
      hrid: 'CCT0017',
      person: _getPDataById('XXPE0017', pList),
      phone: '(555) 222-3333',
      cell: '(555) 999-8871',
      email: 'gandalf@metropolisbank.com',
      company: {
        id: 'XXC0001',
        label: 'Metropolis Bank',
      },
      role: { id: 'XXCCR0002', label: 'Engineer' },
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXCCT0029',
      hrid: 'CCT0029',
      person: _getPDataById('XXPE0029', pList),
      phone: '(555) 222-1234',
      cell: '(555) 999-8763',
      email: 'pippin@metropolisbank.com',
      company: {
        id: 'XXC0001',
        label: 'Metropolis Bank',
      },
      role: { id: 'XXCCR0002', label: 'Engineer' },
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXCCT0030',
      hrid: 'CCT0030',
      person: _getPDataById('XXPE0030', pList),
      phone: '(555) 222-2323',
      cell: '(555) 999-8783',
      email: 'meery@metropolisbank.com',
      company: {
        id: 'XXC0001',
        label: 'Metropolis Bank',
      },
      role: { id: 'XXCCR0002', label: 'Engineer' },
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXCCT0031',
      hrid: 'CCT0031',
      person: _getPDataById('XXPE0031', pList),
      phone: '(555) 123-3333',
      cell: '(555) 888-8883',
      email: 'bilbo@bostoncoach.com',
      company: {
        id: 'XXC0001',
        label: 'Metropolis Bank',
      },
      role: { id: 'XXCCR0002', label: 'Engineer' },
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXCCT0032',
      hrid: 'CCT0032',
      company: { id: 'XXC0010', label: 'Travel + Leisure' },
      person: _getPDataById('XXPE0046', pList),
      phone: '(262) 744-9046',
      cell: '(414) 665-8275',
      email: 'viktoriabranz@northwesternmutual.com',
      role: { id: 'XXCCR0001', label: 'Billing' },
      isPrimaryContact: true,
      isBillingContact: false,
      status: { id: 2, label: 'Active' },
      createdAt: '2024-01-25T22:01:00.000Z',
      modifiedAt: '2024-01-25T22:01:00.000Z',
      version: '1',
      canBeDeleted: true,
    },
  ]
  return companyContactListData.sort((a, b) =>
    a.person.name.lastName > b.person.name.lastName ? 1 : -1
  )
}
